import type { ActivitySummary } from '@painpal/contracts';
import { useState } from 'react';
import { useActivity } from '../../api/queries';
import { formatWatts, noLoadLabel } from '../../lib/format';
import { useReducedMotion } from '../../ui/useMediaQuery';
import type { UploadItem, UploadPhase } from './uploadQueue';
import styles from './upload.module.css';

interface Chip { label: string; tone: 'green' | 'amber' | 'hot' }

const CHIP: Record<UploadPhase, Chip> = {
  rejected: { label: 'REJECTED', tone: 'hot' },
  requesting: { label: 'UPLOADING', tone: 'amber' },
  uploading: { label: 'UPLOADING', tone: 'amber' },
  processing: { label: 'PROCESSING', tone: 'amber' },
  processed: { label: 'PROCESSED', tone: 'green' },
  failed: { label: 'FAILED', tone: 'hot' },
};

export function processedSummary(activity: ActivitySummary): string {
  if (activity.load === null) return `${noLoadLabel(activity)} · on your activity list`;
  return `Load ${Math.round(activity.load)} · Weighted Power ${formatWatts(activity.weightedPower)} · already on your chart`;
}

function ProcessedMessage({ activityId }: { activityId: string }) {
  const activity = useActivity(activityId);
  return <>{activity.data === undefined ? 'Processed · reading Load…' : processedSummary(activity.data)}</>;
}

function RowMessage({ item }: { item: UploadItem }) {
  if (item.phase === 'processed') {
    return item.activityId === null ? <>Already in PainPal · matched an existing ride</> : <ProcessedMessage activityId={item.activityId} />;
  }
  if (item.phase === 'processing') return <>Processing… new rides appear within seconds</>;
  if (item.phase === 'requesting' || item.phase === 'uploading') return <>Uploading…</>;
  return <>{item.message}</>;
}

function StatusChip({ chip }: { chip: Chip }) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(chip);
  const [lastLeft, setLastLeft] = useState<Chip | null>(null);
  if (shown.label !== chip.label) {
    setShown(chip);
    setLastLeft(reduced ? null : shown);
  } else if (reduced && lastLeft !== null) {
    setLastLeft(null);
  }
  return (
    <span className={styles.chipStack}>
      {lastLeft !== null && (
        <span key={lastLeft.label} className={styles.chip} data-tone={lastLeft.tone} data-leaving="true" aria-hidden="true" onAnimationEnd={() => setLastLeft(null)}>
          {lastLeft.label}
        </span>
      )}
      <span key={chip.label} className={styles.chip} data-tone={chip.tone} data-entering={lastLeft === null ? undefined : 'true'}>{chip.label}</span>
    </span>
  );
}

function useProcessedFlash(phase: UploadPhase): boolean {
  const [seen, setSeen] = useState(phase);
  const [flash, setFlash] = useState(false);
  if (seen !== phase) {
    setSeen(phase);
    setFlash(phase === 'processed');
  }
  return flash;
}

export function UploadRow({ item, onRetry }: { item: UploadItem; onRetry: (id: string) => void }) {
  const flash = useProcessedFlash(item.phase);
  return (
    <li className={styles.row} data-flash={flash ? 'true' : undefined}>
      <StatusChip chip={CHIP[item.phase]} />
      <span className={styles.info}>
        <span className={styles.file}>{item.name}</span>
        <span className={styles.message}><RowMessage item={item} /></span>
      </span>
      {item.phase === 'failed' && (
        <button type="button" className="btn btn-outline" onClick={() => onRetry(item.id)} aria-label={`Retry ${item.name}`}>RETRY ↻</button>
      )}
    </li>
  );
}
